import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import Quill from 'quill';
import {
  convertAndPastePlainTextToRichText,
  getPlainTextLength,
} from '../../utils/rich-text-editor-util';
import { QuillEditorComponent } from 'ngx-quill';
import { FormControlState } from 'ngrx-forms';
import { Subscription } from 'rxjs';

const FontAttributor = Quill.import('attributors/class/font') as any;
FontAttributor.whitelist = [
  'GothamNarrowBook',
  'sansserif',
  'roboto',
  'cursive',
  'fantasy',
  'monospace',
];
const SizeStyle = Quill.import('attributors/style/size') as any;
SizeStyle.whitelist = ['0.75em', '1em', '1.5em', '2.5em'];
Quill.register(SizeStyle, true);
Quill.register(FontAttributor, true);

@Component({
  standalone: false,
  selector: 'rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
})
export class RichTextEditorComponent implements OnChanges, OnDestroy {
  @ViewChild(QuillEditorComponent, { static: true })
  quillEditor!: QuillEditorComponent;
  @Input() id!: string;
  @Input() name!: string;
  @Input() counter: number | undefined;
  @Output() valueChanged = new EventEmitter<string>();
  @Output() counterChange = new EventEmitter<number>();
  @Input() enableFontStyles: boolean = true;
  @Input() enableHeadersStyles: boolean = true;
  @Input() enableSubSuperStyles: boolean = true;
  @Input() enableAlignStyles: boolean = true;
  @Input() enableHeadingStyles: boolean = true;
  @Input() enableQuotes: boolean = true;
  @Input() enableLink: boolean = true;
  @Input() enableClean: boolean = true;
  @Input() set ngrxFormControlState(state: FormControlState<any>) {
    this.isTouched = state.isTouched;
    this.isInvalid = state.isInvalid;
    this.isDisabled = state.isDisabled;
  }

  @Output() focus = new EventEmitter<void>();

  isTouched: boolean = false;
  isInvalid: boolean = false;
  isDisabled: boolean = false;
  oldValueConverted: boolean = false;

  public editorModules: any = {};
  private readonly subscription = new Subscription();

  handleFocus() {
    this.focus.emit();
  }

  ngOnChanges() {
    this.updateEditorModules();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  updateEditorModules() {
    this.editorModules = {
      toolbar: [
        this.enableFontStyles
          ? ['bold', 'italic', 'underline', 'strike']
          : null,
        this.enableHeadersStyles ? [{ header: 1 }, { header: 2 }] : null,
        this.enableQuotes ? ['blockquote', 'code-block'] : null,
        this.enableFontStyles
          ? [{ size: ['0.75em', '1em', '1.5em', '2.5em'] }]
          : null,
        this.enableHeadingStyles ? [{ header: [1, 2, 3, 4, 5, 6, false] }] : null,
        this.enableFontStyles ? [{ color: [] }, { background: [] }] : null,
        this.enableFontStyles
          ? [{ list: 'ordered' }, { list: 'bullet' }]
          : null,
        this.enableAlignStyles ? [{ align: [] }] : null,
        this.enableLink ? ['link'] : null,
        this.enableSubSuperStyles ? [{ script: 'sub' }, { script: 'super' }] : null,
        this.enableClean ? ['clean'] : null,
        this.enableFontStyles
          ? [
              {
                font: [
                  'GothamNarrowBook',
                  'sansserif',
                  'roboto',
                  'cursive',
                  'fantasy',
                  'monospace',
                ],
              },
            ]
          : null,
      ].filter(Boolean),
    };
  }

  onEditorCreated(editor: Quill) {
    this.oldValueConverted = false;
    this.counter = this.calculateLength(editor);
    this.onCounterChange(this.counter);

    editor.on('editor-change', () => {
      this.onEditorChange(editor);
    });

    const editorElement = editor.root;
    editorElement.addEventListener('focus', () => {
      this.handleFocus();
    });
  }

  setValue(value: string) {
    this.quillEditor.writeValue(value);
  }

  setDisabledState(isDisabled: boolean) {
    this.quillEditor.setDisabledState(isDisabled);
  }

  private onEditorChange(editor: Quill) {
    let html = editor.getSemanticHTML();
    // check if is empty paragraph
    if (
      html === '<p></p>' ||
      html === '<div></div>' ||
      html === '<p><br></p>' ||
      html === '<div><br></div>'
    ) {
      html = '';
    }

    if (!this.oldValueConverted && !this.isDisabled) {
      this.oldValueConverted = true;
      convertAndPastePlainTextToRichText(editor, html);
    }
    this.counter = this.calculateLength(editor);
    this.onCounterChange(this.counter);
    this.valueChanged.emit(html);
  }

  registerOnTouched(fn: () => {}) {
    this.quillEditor.registerOnTouched(fn);
    this.subscription.add(
      this.quillEditor.onBlur.subscribe(() => {
        fn();
      })
    );
  }

  private calculateLength(editor: Quill) {
    if (editor) {
      return getPlainTextLength(editor.getSemanticHTML());
    }

    return 0;
  }

  private onCounterChange(newCounter: number) {
    this.counter = newCounter;
    this.counterChange.emit(this.counter);
  }
}
